import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useSubscription } from "../../hooks/useSubscription";
import { firebaseApp } from "../../firebase";
import { getFirestore, doc, getDoc, onSnapshot, setDoc, addDoc, collection, Timestamp, updateDoc } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import {
  CheckCircle,
  Trophy,
  BarChart3,
  MessageSquare,
  Star,
  ArrowLeft,
  Award,
  Loader,
  BookOpen,
  Sparkles,
  ChevronDown,
  ChevronUp,
  Lock,
} from "lucide-react";
import toast from "react-hot-toast";
import Tooltip from "../../components/Tooltip/JobSeekerTooltip";
import UpgradeModal from "../../components/UpgradeModal";

const InterviewResults = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { isPro, loading: subscriptionLoading } = useSubscription();
  const db = getFirestore(firebaseApp);

  const [results, setResults] = useState(null);
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showVerificationPrompt, setShowVerificationPrompt] = useState(
    searchParams.get("showVerificationPrompt") === "true"
  );

  // Course generation states
  const [generatingCourse, setGeneratingCourse] = useState(false);
  const [courseId, setCourseId] = useState(null);
  const [courseGenerationStatus, setCourseGenerationStatus] = useState("");
  const [courseGenerationAttempted, setCourseGenerationAttempted] = useState(false);

  // Pro feature states
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeModalFeature, setUpgradeModalFeature] = useState("this feature");
  const [showFullSummary, setShowFullSummary] = useState(false);

  // AI feedback generation states
  const [generatingAIFeedback, setGeneratingAIFeedback] = useState(false);
  const [aiFeedbackGenerated, setAiFeedbackGenerated] = useState(false);

  useEffect(() => {
    if (!sessionId || !user) {
      setLoading(false);
      setError("Invalid session or user.");
      return;
    }

    // First, get the session document to find the testId
    const getSessionAndListenForResults = async () => {
      try {
        const sessionDocRef = doc(db, "interviewSessions", sessionId);
        const sessionDoc = await getDoc(sessionDocRef);

        if (!sessionDoc.exists()) {
          setError("Interview session not found.");
          setLoading(false);
          return;
        }

        const sessionData = sessionDoc.data();
        const testId = sessionData.testId;
        const resultDocId = `${user.id}_${testId}`;
        const resultDocRef = doc(db, "jobseekerResults", resultDocId);

        // Now, set up a real-time listener for the results document
        const unsubscribe = onSnapshot(
          resultDocRef,
          (docSnapshot) => {
            if (docSnapshot.exists()) {
              const resultData = docSnapshot.data() ;

              // Only stop loading once we have the final scores
              if (
                resultData.hasCompletedInterview &&
                typeof resultData.totalScore === "number"
              ) {
                setResults(resultData);

                if (resultData.jobFitAssessment) {
                  try {
                    const parsedAssessment = JSON.parse(
                      resultData.jobFitAssessment
                    );
                    setAssessment(parsedAssessment);
                  } catch (err) {
                    console.error("Error parsing assessment:", err);
                  }
                }
                setLoading(false);
                setError(null);
              }
            } else {
              // The document might not be created yet, keep loading
              setError("Waiting for test results to be saved...");
            }
          },
          (err) => {
            console.error("Error listening to results:", err);
            setError("Failed to load results in real-time.");
            setLoading(false);
          }
        );

        return () => unsubscribe(); // Cleanup listener on component unmount
      } catch (err) {
        console.error("Error setting up results listener:", err);
        setError("Failed to initialize results loading.");
        setLoading(false);
      }
    };

    getSessionAndListenForResults();
  }, [sessionId, user, db]);

  // Generate personalized course after test completion (only for Pro users)
  useEffect(() => {
    if (results && assessment && !courseGenerationAttempted && results.hasCompletedInterview && isPro && !subscriptionLoading) {
      console.log("Checking if course generation needed...", {
        hasResults: !!results,
        hasAssessment: !!assessment,
        totalScore: results.totalScore,
        mcqScore: results.mcqScore,
        interviewScore: results.interviewScore,
        hasQuestions: !!results.questions,
        hasWeakAreas: !!assessment.weakAreas,
        isPro: isPro
      });

      // Only auto-generate course for Pro users with imperfect scores
      const shouldGenerateCourse = results.totalScore < 100;

      if (shouldGenerateCourse) {
        console.log("✅ Generating course for Pro user with score:", results.totalScore);
        setCourseGenerationAttempted(true);
        generateCourse(results, assessment);
      } else {
        console.log("❌ Score is perfect (100), no course needed");
      }
    }
  }, [results, assessment, courseGenerationAttempted, isPro, subscriptionLoading]);

  const generateCourse = async (resultData, assessmentData) => {
    setGeneratingCourse(true);
    setCourseGenerationStatus("Analyzing your performance...");

    try {
      const auth = getAuth(firebaseApp);
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("Not authenticated");
      const idToken = await currentUser.getIdToken();

      console.log("Generating HR test course...");

      // Prepare MCQ mistakes
      let mcqMistakes = [];
      let weakAreasFromMcq = [];

      if (resultData.questions && resultData.correctAnswerIndices && resultData.userAnswerIndices) {
        console.log("Analyzing MCQ mistakes...");
        const mistakesResponse = await fetch(
          "https://us-central1-vetriaai-mvp.cloudfunctions.net/analyzeTestMistakes",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${idToken}`,
            },
            body: JSON.stringify({
              testQuestions: resultData.questions,
              userAnswers: resultData.userAnswerIndices,
              correctAnswers: resultData.correctAnswerIndices,
            }),
          }
        );

        if (mistakesResponse.ok) {
          const mistakesAnalysis = await mistakesResponse.json();
          console.log("MCQ mistakes analyzed:", mistakesAnalysis);
          mcqMistakes = mistakesAnalysis.mistakes || [];
          weakAreasFromMcq = mistakesAnalysis.weakAreas || [];
        } else {
          console.warn("MCQ analysis failed, continuing with interview data only");
        }
      }

      // Combine weak are both MCQ and AI interview
      const weakAreasFromInterview = assessmentData.weakAreas || [];

      // If no weakAre interview, extract topics from weaknesses
      const weakAreasExtracted = weakAreasFromInterview.length > 0
        ? weakAreasFromInterview
        : (assessmentData.weaknesses || []).map(w => {
            // Extract key topic from weakness description
            const match = w.match(/^([^-:]+)/);
            return match ? match[1].trim() : w.substring(0, 50);
          }).slice(0, 5); // Take first 5 weaknesses 

      const combinedWeakAreas = [...new Set([...weakAreasFromMcq, ...weakAreasExtracted])];

      console.log("Combined weak areas:", combinedWeakAreas);
      console.log("MCQ weak areas:", weakAreasFromMcq);
      console.log("Interview weak areas:", weakAreasExtracted);

      // Store combined mistakes analysis
      setCourseGenerationStatus("Creating your personalized learning course...");
      console.log("Storing mistakes analysis...");

      await setDoc(doc(db, "testMistakes", `${user?.id}_${resultData.testId}`), {
        userId: user?.id,
        testResultId: `${user?.id}_${resultData.testId}`,
        mcqMistakes: mcqMistakes,
        interviewWeakAreas: weakAreasFromInterview,
        weakAreas: combinedWeakAreas,
        testType: "hr_test",
        createdAt: Timestamp.now(),
      });

      // Generate personalized course
      console.log("Calling generatePersonalizedCourse function...");
      const courseResponse = await fetch(
        "https://us-central1-vetriaai-mvp.cloudfunctions.net/generatePersonalizedCourse",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            mistakes: mcqMistakes,
            weakAreas: combinedWeakAreas,
            recommendedFocus: combinedWeakAreas.slice(0, 5), // Must be an array, not a string
          }),
        }
      );

      if (!courseResponse.ok) {
        const errorText = await courseResponse.text();
        console.error("generatePersonalizedCourse failed:", errorText);
        throw new Error(`Failed to generate course: ${errorText}`);
      }

      const courseData = await courseResponse.json();
      console.log("Personalized course generated successfully");

      // Store personalized course
      console.log("Storing personalized course in Firestore...");
      const courseRef = await addDoc(collection(db, "personalizedCourses"), {
        userId: user?.id,
        testResultId: `interview:${user?.id}_${resultData.testId}`,
        courseData: courseData.course,
        basedOnMistakes: combinedWeakAreas,
        weakAreasLabels: combinedWeakAreas,
        testType: "hr_test",
        jobTitle: resultData.jobTitle,
        completed: false,
        courseTestPassed: false,
        createdAt: Timestamp.now(),
      });

      console.log("Personalized course stored successfully with ID:", courseRef.id);
      setCourseId(courseRef.id);
      setGeneratingCourse(false);
      toast.success("🎉 Your personalized learning course is ready! Check the 'My Courses' tab.");
    } catch (error) {
      console.error("Error generating course:", error);
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      toast.error(`Course generation failed: ${errorMessage}`);
      setGeneratingCourse(false);
    }
  };

  // Generate AI feedback (strengths, are improvement, performance summary)
  const generateAIFeedback = async (resultData, assessmentData) => {
    if (resultData.aiFeedbackGenerated) {
      console.log("AI feedback already generated, skipping...");
      setAiFeedbackGenerated(true);
      return;
    }

    setGeneratingAIFeedback(true);
    console.log("Generating AI feedback for test results...");

    try {
      const auth = getAuth(firebaseApp);
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("Not authenticated");
      const idToken = await currentUser.getIdToken();

      // Call the AI feedback generation function
      const response = await fetch(
        "https://us-central1-vetriaai-mvp.cloudfunctions.net/generateTestFeedback",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            mcqScore: resultData.mcqScore,
            interviewScore: resultData.interviewScore,
            totalScore: resultData.totalScore,
            jobTitle: resultData.jobTitle,
            assessment: assessmentData,
            questions: resultData.questions,
            userAnswers: resultData.userAnswerIndices,
            correctAnswers: resultData.correctAnswerIndices,
          }),
        }
      );

      if (response.ok) {
        const feedbackData = await response.json();
        console.log("AI feedback generated:", feedbackData);

        // Update the results document with AI feedback
        const resultDocId = `${user?.id}_${resultData.testId}`;
        await updateDoc(doc(db, "jobseekerResults", resultDocId), {
          aiStrengths: feedbackData.strengths || [],
          aiAreasForImprovement: feedbackData.areasForImprovement || [],
          aiPerformanceSummary: feedbackData.performanceSummary || "",
          aiPerformanceSummaryShort: feedbackData.performanceSummaryShort || "",
          aiFeedbackGenerated: true,
        });

        // Update local state
        setResults(prev => prev ? {
          ...prev,
          aiStrengths: feedbackData.strengths || [],
          aiAreasForImprovement: feedbackData.areasForImprovement || [],
          aiPerformanceSummary: feedbackData.performanceSummary || "",
          aiPerformanceSummaryShort: feedbackData.performanceSummaryShort || "",
          aiFeedbackGenerated: true,
        } : null);

        setAiFeedbackGenerated(true);
        console.log("AI feedback stored successfully");
      } else {
        console.warn("AI feedback generation failed, using fallback");
        // Use assessment data 
        const fallbackStrengths = assessmentData.strengths?.slice(0, 3) || [];
        const fallbackWeaknesses = assessmentData.weaknesses?.slice(0, 3) || [];
        const fallbackSummary = assessmentData.narrativeAssessment || "Performance analysis not available.";
        const fallbackShort = fallbackSummary.split('.')[0] + '.';

        const resultDocId = `${user?.id}_${resultData.testId}`;
        await updateDoc(doc(db, "jobseekerResults", resultDocId), {
          aiStrengths: fallbackStrengths,
          aiAreasForImprovement: fallbackWeaknesses,
          aiPerformanceSummary: fallbackSummary,
          aiPerformanceSummaryShort: fallbackShort,
          aiFeedbackGenerated: true,
        });

        setResults(prev => prev ? {
          ...prev,
          aiStrengths: fallbackStrengths,
          aiAreasForImprovement: fallbackWeaknesses,
          aiPerformanceSummary: fallbackSummary,
          aiPerformanceSummaryShort: fallbackShort,
          aiFeedbackGenerated: true,
        } : null);

        setAiFeedbackGenerated(true);
      }
    } catch (error) {
      console.error("Error generating AI feedback:", error);
      // Use assessment data  on error
      const fallbackStrengths = assessmentData.strengths?.slice(0, 3) || [];
      const fallbackWeaknesses = assessmentData.weaknesses?.slice(0, 3) || [];

      setResults(prev => prev ? {
        ...prev,
        aiStrengths: fallbackStrengths,
        aiAreasForImprovement: fallbackWeaknesses,
        aiPerformanceSummary: assessmentData.narrativeAssessment || "",
        aiPerformanceSummaryShort: (assessmentData.narrativeAssessment || "").split('.')[0] + '.',
        aiFeedbackGenerated: true,
      } : null);
      setAiFeedbackGenerated(true);
    } finally {
      setGeneratingAIFeedback(false);
    }
  };

  // Trigger AI feedback generation when results are ready
  useEffect(() => {
    if (results && assessment && results.hasCompletedInterview && !aiFeedbackGenerated && !generatingAIFeedback) {
      generateAIFeedback(results, assessment);
    }
  }, [results, assessment, aiFeedbackGenerated, generatingAIFeedback]);

  // Handle Generate Course button click with Pro check
  const handleGenerateCourseClick = () => {
    if (!isPro) {
      setUpgradeModalFeature("AI Course Generation");
      setShowUpgradeModal(true);
      return;
    }
    if (results && assessment) {
      setCourseGenerationAttempted(true);
      generateCourse(results, assessment);
    }
  };

  // Handle Show More click with Pro check
  const handleShowMoreClick = () => {
    if (!isPro) {
      setUpgradeModalFeature("Detailed Performance Summary");
      setShowUpgradeModal(true);
      return;
    }
    setShowFullSummary(true);
  };

  const getScoreColor = (score, maxScore) => {
    const percentage = (score / maxScore) * 100;
    if (percentage >= 80) return "text-green-600";
    if (percentage >= 60) return "text-yellow-600";
    return "text-red-600";
  };

  const getScoreBgColor = (score, maxScore) => {
    const percentage = (score / maxScore) * 100;
    if (percentage >= 80) return "bg-green-100";
    if (percentage >= 60) return "bg-yellow-100";
    return "bg-red-100";
  };

  const getBadge = (totalScore) => {
    if (totalScore >= 90) return { text: "Gold", color: "bg-yellow-500" };
    if (totalScore >= 75) return { text: "Silver", color: "bg-gray-400" };
    if (totalScore >= 60) return { text: "Bronze", color: "bg-amber-600" };
    return { text: "Participant", color: "bg-gray-500" };
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 mb-4">{error}</div>
          <button
            onClick={() => navigate("/job-seeker")}
            className="px-4 py-2 bg-red-800 text-white rounded-lg hover:bg-red-900"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Use a loading screen for the initial data fetch OR if results are not finalized
  if (loading || !results) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Finalizing your results...</p>
          <p className="text-sm text-gray-500 mt-2">
            The AI is assessing your interview. This may take a moment.
          </p>
        </div>
      </div>
    );
  }

  const badge = getBadge(results.totalScore);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Verification Prompt Modal for HR-shared test completion */}
      {showVerificationPrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Trophy className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              Awesome Work!
            </h2>
            <p className="text-gray-600 mb-6">
              You've completed the full assessment including the AI interview! To unlock more job opportunities and get noticed by other recruiters, complete your skills verification.
            </p>
            <div className="space-y-3">
              <button
                onClick={() => {
                  setShowVerificationPrompt(false);
                  navigate("/job-seeker/skills-validation");
                }}
                className="w-full px-6 py-3 bg-red-800 text-white rounded-lg hover:bg-red-900 transition-colors font-medium inline-flex items-center justify-center gap-2"
              >
                <Sparkles className="w-5 h-5" />
                Get Verified Now
              </button>
              <button
                onClick={() => setShowVerificationPrompt(false)}
                className="w-full px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Maybe Later
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-4">
              Verified candidates get better visibility to recruiters
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Test Results</h1>
            <p className="text-gray-600 mt-1">{results.jobTitle}</p>
          </div>
          <button
            onClick={() => navigate("/job-seeker")}
            className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6 space-y-6">
        {/* Overall Score Card */}
        <div className="bg-gradient-to-r from-red-800 to-red-600 text-white rounded-xl p-8">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-3xl font-bold mb-2">
                {(results.totalScore || 0).toFixed(1)}/100
              </h2>
              <p className="text-red-100 text-lg">Overall Score</p>
              <div className="flex items-center space-x-2 mt-4">
                <div
                  className={`px-3 py-1 rounded-full text-sm font-medium text-white ${badge.color}`}
                >
                  <div className="flex items-center space-x-1">
                    <Award className="w-4 h-4" />
                    <span>{badge.text} Badge</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="text-center">
              <Trophy className="w-16 h-16 text-yellow-300 mx-auto mb-2" />
              <p className="text-red-100">Assessment Complete</p>
            </div>
          </div>
        </div>

        {/* Score Breakdown */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Round Scores */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Round Breakdown
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      Round 1: MCQ Test
                    </p>
                    <p className="text-sm text-gray-600">
                      Multiple Choice Questions
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p
                    className={`text-xl font-bold ${getScoreColor(
                      results.mcqScore || 0,
                      25
                    )}`}
                  >
                    {(results.mcqScore || 0).toFixed(1)}/25
                  </p>
                  <p className="text-sm text-gray-500">25% weight</p>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      Round 2: AI Interview
                    </p>
                    <p className="text-sm text-gray-600">Technical Interview</p>
                  </div>
                </div>
                <div className="text-right">
                  <p
                    className={`text-xl font-bold ${getScoreColor(
                      results.interviewScore || 0,
                      75
                    )}`}
                  >
                    {(results.interviewScore || 0).toFixed(1)}/75
                  </p>
                  <p className="text-sm text-gray-500">75% weight</p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Performance Summary
            </h3>

            {/* AI-generated Performance Summary */}
            {generatingAIFeedback ? (
              <div className="flex items-center space-x-2 text-gray-500 mb-4">
                <Loader className="w-4 h-4 animate-spin" />
                <span className="text-sm">Generating AI performance summary...</span>
              </div>
            ) : (
              <div className="mb-4">
                <p className="text-gray-700 text-sm">
                  {showFullSummary && isPro
                    ? results.aiPerformanceSummary || assessment?.narrativeAssessment || "Performance analysis not available."
                    : results.aiPerformanceSummaryShort || (assessment?.narrativeAssessment?.split('.')[0] + '.') || "Performance analysis not available."
                  }
                </p>
                {!showFullSummary && (results.aiPerformanceSummary || assessment?.narrativeAssessment) && (
                  <button
                    onClick={handleShowMoreClick}
                    className="mt-2 text-sm text-red-800 hover:text-red-900 font-medium inline-flex items-center gap-1"
                  >
                    {isPro ? (
                      <>
                        Show More <ChevronDown className="w-4 h-4" />
                      </>
                    ) : (
                      <>
                        <Lock className="w-3 h-3" /> Show More (Pro)
                      </>
                    )}
                  </button>
                )}
                {showFullSummary && isPro && (
                  <button
                    onClick={() => setShowFullSummary(false)}
                    className="mt-2 text-sm text-red-800 hover:text-red-900 font-medium inline-flex items-center gap-1"
                  >
                    Show Less <ChevronUp className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}

            <div className="space-y-4 pt-4 border-t border-gray-100">
              {assessment?.structuredAssessment ? (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Technical Skills</span>
                    <span className="font-medium">
                      {assessment.structuredAssessment.technical || "N/A"}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Communication</span>
                    <span className="font-medium">
                      {assessment.structuredAssessment.communication || "N/A"}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Problem Solving</span>
                    <span className="font-medium">
                      {assessment.structuredAssessment.problemSolving || "N/A"}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Job Fit</span>
                    <span className="font-medium">
                      {assessment.structuredAssessment.jobFit || "N/A"}
                    </span>
                  </div>
                </>
              ) : (
                <p className="text-gray-500 text-sm">
                  Detailed performance metrics are being generated...
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Feedback - AI-generated Strengths and Are Improvement */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Your Feedback
          </h3>

          {generatingAIFeedback ? (
            <div className="flex items-center space-x-2 text-gray-500">
              <Loader className="w-4 h-4 animate-spin" />
              <span className="text-sm">AI is analyzing your performance...</span>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <h4 className="font-medium text-green-800 mb-2 flex items-center">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Strengths
                </h4>
                <ul className="space-y-2">
                  {(results.aiStrengths && results.aiStrengths.length > 0) ? (
                    results.aiStrengths.slice(0, 3).map((strength, index) => (
                      <li
                        key={index}
                        className="text-sm text-gray-700 flex items-start"
                      >
                        <Star className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        {strength}
                      </li>
                    ))
                  ) : assessment?.strengths?.length > 0 ? (
                    assessment.strengths.slice(0, 3).map((strength, index) => (
                      <li
                        key={index}
                        className="text-sm text-gray-700 flex items-start"
                      >
                        <Star className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        {strength}
                      </li>
                    ))
                  ) : (
                    <li className="text-sm text-gray-500">
                      No specific strengths identified.
                    </li>
                  )}
                </ul>
              </div>

              <div>
                <h4 className="font-medium text-orange-800 mb-2 flex items-center">
                  <Star className="w-4 h-4 mr-2" />
                  Are Improvement
                </h4>
                <ul className="space-y-2">
                  {(results.aiAreasForImprovement && results.aiAreasForImprovement.length > 0) ? (
                    results.aiAreasForImprovement.slice(0, 3).map((area, index) => (
                      <li
                        key={index}
                        className="text-sm text-gray-700 flex items-start"
                      >
                        <span className="w-3 h-3 border border-orange-400 rounded-full mr-2 mt-1 flex-shrink-0"></span>
                        {area}
                      </li>
                    ))
                  ) : assessment?.weaknesses?.length > 0 ? (
                    assessment.weaknesses.slice(0, 3).map((weakness, index) => (
                      <li
                        key={index}
                        className="text-sm text-gray-700 flex items-start"
                      >
                        <span className="w-3 h-3 border border-orange-400 rounded-full mr-2 mt-1 flex-shrink-0"></span>
                        {weakness}
                      </li>
                    ))
                  ) : (
                    <li className="text-sm text-gray-500">
                      No specific are improvement identified.
                    </li>
                  )}
                </ul>
              </div>
            </>
          )}
        </div>

        {/* Personalized Course Generation */}
        {generatingCourse && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
            <div className="flex items-center space-x-4">
              <Loader className="w-6 h-6 text-blue-600 animate-spin" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {courseGenerationStatus}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  We're creating a personalized learning course based on your performance. This will help you improve in are you need more practice.
                </p>
              </div>
            </div>
          </div>
        )}

        {courseId && !generatingCourse && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <BookOpen className="w-8 h-8 text-green-600" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Your Personalized Course is Ready!
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Based on your test performance, we've created a custom learning path to help you improve.
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate(`/job-seeker/personalized-course/${courseId}`)}
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold"
              >
                Start Learning
              </button>
            </div>
          </div>
        )}

        {/* Manual Course Generation Button (if course not generated yet) */}
        {!generatingCourse && !courseId && results && assessment && results.totalScore < 100 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <BookOpen className="w-8 h-8 text-yellow-600" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Want to Improve Your Score?
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Generate a personalized learning course based on your test performance and weak areas.
                  </p>
                </div>
              </div>
              <button
                onClick={handleGenerateCourseClick}
                className="px-6 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors font-semibold whitespace-nowrap inline-flex items-center gap-2"
              >
                {!isPro && <Lock className="w-4 h-4" />}
                Generate Course
              </button>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                What's Next?
              </h3>
              <p className="text-gray-600 mt-1">
                Your results have been shared with the hiring team.
              </p>
            </div>
            <div className="flex space-x-3">
              <Tooltip content="Browse and take more available tests">
                <button
                  onClick={() => navigate("/job-seeker/tests")}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Take More Tests
                </button>
              </Tooltip>
              <Tooltip content="Go to your dashboard">
                <button
                  onClick={() => navigate("/job-seeker")}
                  className="px-4 py-2 bg-red-800 text-white rounded-lg hover:bg-red-900"
                >
                  Dashboard
                </button>
              </Tooltip>
            </div>
          </div>
        </div>
      </div>

      {/* Upgrade Modal for Pro Features */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature={upgradeModalFeature}
      />
    </div>
  );
};

export default InterviewResults;
